import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { fournisseurService } from '../../api/services';
import { Fournisseur } from '../../types';

const FournisseurForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } =
    useForm<Partial<Fournisseur>>({ defaultValues: { actif: true } });

  useEffect(() => {
    if (isEdit && id) {
      fournisseurService.getById(Number(id))
        .then((data) => reset(data))
        .catch(() => toast.error('Erreur de chargement'));
    }
  }, [id, isEdit, reset]);

  const onSubmit = async (data: Partial<Fournisseur>) => {
    try {
      if (isEdit && id) {
        await fournisseurService.update(Number(id), data);
        toast.success('Fournisseur mis à jour');
      } else {
        await fournisseurService.create(data);
        toast.success('Fournisseur créé');
      }
      navigate('/fournisseurs');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur lors de l’enregistrement');
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <h1 className="page-title">{isEdit ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}</h1>

      <form onSubmit={handleSubmit(onSubmit)} className="card space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="label">Raison sociale *</label>
            <input {...register('raisonSociale', { required: 'Champ requis' })}
              className={`input-field ${errors.raisonSociale ? 'input-error' : ''}`} />
            {errors.raisonSociale && <p className="mt-1 text-sm text-red-600">{errors.raisonSociale.message}</p>}
          </div>
          <div>
            <label className="label">Matricule fiscal</label>
            <input {...register('matriculeFiscal')} className="input-field" />
          </div>
          <div>
            <label className="label">Contact</label>
            <input {...register('contactNom')} className="input-field" />
          </div>
          <div className="md:col-span-2">
            <label className="label">Adresse</label>
            <input {...register('adresse')} className="input-field" />
          </div>
          <div>
            <label className="label">Code postal</label>
            <input {...register('codePostal')} className="input-field" />
          </div>
          <div>
            <label className="label">Ville</label>
            <input {...register('ville')} className="input-field" />
          </div>
          <div>
            <label className="label">Téléphone</label>
            <input {...register('telephone')} className="input-field" />
          </div>
          <div>
            <label className="label">Email</label>
            <input type="email" {...register('email')} className="input-field" />
          </div>
          <div>
            <label className="label">RIB</label>
            <input {...register('rib')} className="input-field" />
          </div>
          <div>
            <label className="label">Banque</label>
            <input {...register('banque')} className="input-field" />
          </div>
          <div>
            <label className="label">Délai de paiement (jours)</label>
            <input type="number" {...register('delaiPaiement', { valueAsNumber: true })} className="input-field" />
          </div>
          <div>
            <label className="label">Retenue à la source (%)</label>
            <input type="number" step="0.001" {...register('tauxRetenueSource', { valueAsNumber: true })} className="input-field" />
          </div>
          <div>
            <label className="label">Note qualité (0-5)</label>
            <input type="number" step="0.5" min="0" max="5"
                   {...register('noteQualite', { valueAsNumber: true })} className="input-field" />
          </div>
          <div>
            <label className="label">CO₂ transport / commande (kg)</label>
            <input type="number" step="0.001" min="0"
                   {...register('transportCo2ParCommande', { valueAsNumber: true })} className="input-field" />
          </div>
        </div>

        <label className="flex items-center gap-2">
          <input type="checkbox" {...register('actif')} />
          <span className="text-sm text-gray-700">Actif</span>
        </label>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => navigate('/fournisseurs')}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
            Annuler
          </button>
          <button type="submit" disabled={isSubmitting} className="btn-primary">
            {isSubmitting ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FournisseurForm;
